'use client'

import { useContextStore } from '@fukku/price/utils/useContextStore/useContextStore'
import { createContext, type ReactNode, useContext, useRef } from 'react'
import type { CountryConfiguration } from 'types/countryConfiguration'
import type { Channel } from 'types/masterData'
import { useStore } from 'zustand'
import type { StoreApi } from 'zustand/vanilla'
import {
	createPricesStore,
	type Price,
	type PricesStore,
} from '../store/pricesStore/pricesStore'
import type { ExtraPriceProps, PriceClasses } from '../types'
import { getPriceClasses } from '../utils/getPriceClasses/getPriceClasses'

export type OmnibusPriceFF = {
	enabled: boolean
	variables: {
		first_level_price_countries?: {
			countries: string[]
		}
	}
}

type PricesProviderProps = {
	readonly children: ReactNode
	readonly price: Price
	readonly crossedOutPrices?: Price[]
	readonly allCrossedOutWithoutLowest?: Price[]
	readonly discountRate?: number
	readonly showAdditionalCurrencies?: boolean
	readonly showDiscountRate?: boolean
	readonly classes?: PriceClasses
	readonly hideSaleOrPromoPrice: boolean
	readonly country: CountryConfiguration
	readonly channel: Channel
} & Omit<ExtraPriceProps, 'classes' | 'showOmnibusPrice' | 'hidePriceHistory'>

export const PricesContext = createContext<StoreApi<PricesStore> | null>(null)

export const usePricesStore = () => {
	const context = useContext(PricesContext)
	if (context == null) {
		throw new Error('usePricesStore must be used within a PricesProvider')
	}
	return useStore(context)
}

export const PricesProvider = ({
	price,
	crossedOutPrices,
	allCrossedOutWithoutLowest,
	discountRate,
	classes,
	showDiscountRate,
	hideSaleOrPromoPrice,
	country,
	channel,
	children,
	...extraPriceProps
}: PricesProviderProps) => {
	const priceRef = useRef<HTMLDivElement | null>(null)
	const priceClasses = getPriceClasses(classes)

	const initialPrice = {
		price,
		crossedOutPrices,
		allCrossedOutWithoutLowest,
		discountRate,
		hideSaleOrPromoPrice,
		country,
		channel,
		priceRef,
		priceClasses,
		showDiscountRate,
		...extraPriceProps,
	}

	const pricesStore = useContextStore(createPricesStore, initialPrice)

	return (
		<PricesContext.Provider value={pricesStore}>
			{children}
		</PricesContext.Provider>
	)
}
